import { Tab } from '@aibees/shared/components';
import { PresetVisibility } from '@aibees/shared/definition';
import { useAdvanceTableDataFiltersContext } from '@aibees/shared/features';
import { classnames } from '@aibees/shared/utils';
import { Tabs, TabsProps } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';

export interface CardVisibilityTabsProps {
  className?: string;
  // Label for the INTERNAL visibility tab. Defaults to "Team".
  internalLabel?: string;
  counts?: {
    private?: number;
    internal?: number;
  };
}

export const CardVisibilityTabs = memo(function CardVisibilityTabs({
  className,
  internalLabel = 'Team',
  counts,
}: CardVisibilityTabsProps) {
  const { filters, setFilter } = useAdvanceTableDataFiltersContext();
  const [value, setValue] = useState<PresetVisibility>(PresetVisibility.ALL);

  useEffect(() => {
    if (filters.visibility) setFilter('visibility', undefined);
    // Reset visibility to "All" whenever this tab mounts (i.e. main tab change).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = useCallback<NonNullable<TabsProps['onChange']>>(
    (_event, newValue: PresetVisibility) => {
      setValue(newValue);
      setFilter('visibility', newValue || undefined);
    },
    [setFilter]
  );

  const TABS = useMemo(
    () => [
      { label: 'All', value: PresetVisibility.ALL },
      { label: 'Mine', value: PresetVisibility.PRIVATE },
      { label: internalLabel, value: PresetVisibility.INTERNAL },
    ],
    [internalLabel]
  );

  const labelContent = useMemo(() => {
    if (!counts) return null;
    const priv = counts.private ?? 0;
    const internal = counts.internal ?? 0;
    return {
      [PresetVisibility.ALL]: priv + internal,
      [PresetVisibility.PRIVATE]: priv,
      [PresetVisibility.INTERNAL]: internal,
    } as Record<PresetVisibility, number>;
  }, [counts]);

  return (
    <Tabs
      className={classnames('CardVisibilityTabs', className)}
      value={value}
      onChange={handleChange}
      variant="fullWidth"
    >
      {TABS.map((t) => (
        <Tab
          key={t.value}
          label={t.label}
          value={t.value}
          LabelContent={
            labelContent ? String(labelContent[t.value]) : undefined
          }
          LabelVariant="soft"
        />
      ))}
    </Tabs>
  );
});
